import { Button, FormLabel } from '@mui/material'
import { ColumnDef } from '@tanstack/react-table'
import { useEffect } from 'react'
import { FieldArrayWithId, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { BoxForm, Select } from 'components/Form'
import { Table } from 'components/Table'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { NEW_VIEWPORT_ID_FIELD } from '../../../../constants'
import { Schema } from '../../../../schema'
import { useRefreshSizeViewports } from '../../hooks/useRefreshSizeViewports/useRefreshSizeViewports'
import { useColumns } from './hooks/useColumns'

export const ViewportsTable = (): React.JSX.Element => {
  const { t } = useTranslation(['features/adUnit', 'common'])
  const ability = useAbility()

  const { clearErrors, control, setError, setValue, watch } = useFormContext<Schema>()
  const {
    refreshRedirect: {
      mediaTypeRefreshers: { newViewportId },
    },
  } = watch()

  const { fields, insert, remove } = useFieldArray({
    control,
    name: 'refreshRedirect.mediaTypeRefreshers.viewportAttributes',
  })

  useEffect(() => {
    if (fields.length === 0) {
      setError(NEW_VIEWPORT_ID_FIELD, {
        message: t('features/adUnit:form.refresher.refreshRedirect.validation.viewportRequired'),
      })
      return
    }
    clearErrors(NEW_VIEWPORT_ID_FIELD)
  }, [fields.length])

  const { getNewViewportData, options } = useRefreshSizeViewports()

  const columns: ColumnDef<FieldArrayWithId<Schema>>[] = useColumns({ remove })
  const addDisabled = options.length === 0
  const cannotUpdateAdUnit = ability.cannot('update', 'AdUnit')
  const noOptionSelected: boolean = newViewportId === undefined || newViewportId === ''

  const AddButton: React.JSX.Element = (
    <Button
      data-cy='viewport-add'
      disabled={cannotUpdateAdUnit || addDisabled || noOptionSelected}
      onClick={(): void => {
        const result = getNewViewportData()

        if (result !== null) {
          const [index, schema] = result
          insert(index, schema)
          setValue(NEW_VIEWPORT_ID_FIELD, '')
        }
      }}
      variant='contained'
    >
      {t('common:form.action.add')}
    </Button>
  )

  return (
    <>
      <BoxForm sx={{ pb: CONTAINER_SPACING }}>
        <FormLabel>
          {t(
            'features/adUnit:form.refresher.refreshRedirect.columns.refreshSizes.viewportList.add.title',
          )}
        </FormLabel>

        <FlexBox
          axis='x'
          sx={{ alignItems: 'baseline', gap: 2 }}
        >
          <Select
            control={control}
            disabled={cannotUpdateAdUnit || addDisabled}
            label={t(
              'features/adUnit:form.refresher.refreshRedirect.columns.refreshSizes.viewportList.add.label',
            )}
            name={NEW_VIEWPORT_ID_FIELD}
            options={options}
          />
          {AddButton}
        </FlexBox>
      </BoxForm>

      <Table<FieldArrayWithId<Schema>>
        columns={columns}
        data={fields}
      />
    </>
  )
}
